import React, { useEffect, useMemo, useState } from "react";
import { useLocalization } from "@progress/kendo-react-intl";
import moment from "moment";
import { useQuery } from "react-query";
import { useWindowSize } from "usehooks-ts";
import { enMessages } from "../../../../../messages/en-US";
import type { EndpointDetailsDto } from "../../../../../Models/Dashboard/SelectedAssetMetricsReadDto";
import type { AnomalyBandWithForm } from "../Overview/Dialogs/AnomalyDetector/AnomalyBand/types";
import { isOngoingEndDate, normalizeCriticality } from "../Overview/Dialogs/AnomalyDetector/AnomalyDetectorForm.utils";
import { AnomalyBandProvider } from "../Overview/Dialogs/AnomalyDetector/AnomalyBand/AnomalyBandContext";
import AnomalyDetectorForm from "../Overview/Dialogs/AnomalyDetector/AnomalyDetectorForm";
import EndpointActivityTabPage from "../../../../SiteExplorer/Pages/Endpoints/Activity/EndpointActivityTabPage";
import EndpointDiagnosticsTabPage from "../../../../SiteExplorer/Pages/Endpoints/Diagnostics/EndpointDiagnosticsTabPage";
import siteExplorerApiService from "../../../../SiteExplorer/Utilities/SiteExplorerApiService";
import assetsDashboardApiService from "../../Utilities/AssetsDashboardApiService";
import anomalyApiService from "../../Utilities/AnomalyApiService";
import LoadingSpinner from "../../../../Shared/Components/LoadingSpinner";
import useIdentityStore from "../../../../../Zustand/identityStore";
import type {
  InsightAnchorItem,
  InsightAnomalyItem,
  InsightBackendPayload,
  InsightsAsset,
  MlAnchorDto,
  ProcessedInsightRow,
} from "./types";

interface InsightsAnomalyDetailPanelProps {
  item: ProcessedInsightRow | null;
  asset?: InsightsAsset;
  /** Site-level rows are lean and carry no asset context: fetch the full item and its asset by id. */
  fetchDetailData?: boolean;
}

type InsightsSnapshotChartKind = "activity" | "diagnostics";

const DIAGNOSTICS_STREAMS = new Set(["temperature", "voltage", "rssi"]);
const CHART_CONTEXT_DAYS = 3;

function isBackendAnomaly(anomaly: ProcessedInsightRow["rawAnomaly"]): anomaly is InsightAnomalyItem {
  return "kind" in anomaly && anomaly.kind === "anomaly";
}

function isAnchorItem(anomaly: ProcessedInsightRow["rawAnomaly"]): anomaly is InsightAnchorItem {
  return "kind" in anomaly && anomaly.kind === "anchor";
}

function getInsightsSnapshotChartKind(item: ProcessedInsightRow): InsightsSnapshotChartKind {
  return item.streams.some((stream) => DIAGNOSTICS_STREAMS.has(stream)) ? "diagnostics" : "activity";
}

function toAnomalyBand(item: ProcessedInsightRow): AnomalyBandWithForm {
  const anomaly = item.rawAnomaly;
  const startTime = Math.floor(item.startDate.getTime() / 1000);
  const ongoing = isBackendAnomaly(anomaly) && isOngoingEndDate(item.endDate);
  const endTime = ongoing
    ? Math.floor(Date.now() / 1000)
    : Math.floor(item.endDate.getTime() / 1000);

  if (isBackendAnomaly(anomaly)) {
    const source = anomaly.data;
    const persistedId = source.id || undefined;
    const localBandId = persistedId ?? source.anomalyId ?? `band-${source.pointId}-${source.startDate}`;
    const legacyForm = source.form as typeof source.form & { notes?: AnomalyBandWithForm["notes"] };
    const formCriticality = source.form.criticality;

    return {
      id: localBandId,
      bandType: "anomaly",
      persistedId,
      anomalyId: source.anomalyId || undefined,
      notes: source.notes ?? legacyForm.notes ?? [],
      pendingNotes: [],
      startTime,
      endTime,
      ongoing,
      streams: source.streams ?? [],
      criticality: normalizeCriticality(formCriticality),
      indicators: source.form.indicators ?? [],
      rootCauses: source.form.rootCauses ?? [],
      actions: source.form.actions ?? [],
      confidence: source.form.confidence,
      savings: source.form.estimatedSavings,
      userNotes: "",
      createdByUserId: source.userId,
      createdByUserName: source.user,
      mlResults: source.mlResults,
    };
  }

  if (isAnchorItem(anomaly)) {
    const source = anomaly.data;
    const persistedId = source.id || undefined;
    const localBandId = persistedId ?? source.anomalyId ?? `anchor-${source.pointId}-${source.startDate}`;
    const formCriticality = source.form.criticality;

    return {
      id: localBandId,
      bandType: "anchor",
      persistedId,
      anomalyId: source.anomalyId || undefined,
      notes: source.notes ?? [],
      pendingNotes: [],
      revisionHistory: source.revisionHistory ?? [],
      startTime,
      endTime,
      streams: source.streams ?? [],
      criticality: normalizeCriticality(formCriticality) || undefined,
      indicators: source.form.indicators ?? [],
      rootCauses: source.form.rootCauses ?? [],
      actions: source.form.actions ?? [],
      mlResults: source.mlResults,
      confidence: source.form.confidence ?? undefined,
      savings: source.form.estimatedSavings ?? undefined,
      userNotes: "",
      createdByUserId: source.userId || undefined,
      createdByUserName: source.user || undefined,
    };
  }

  return {
    id: anomaly.id,
    bandType: "anomaly",
    notes: anomaly.notes
      ? [
          {
            timestamp: new Date().toISOString(),
            user: anomaly.author ?? "",
            comment: anomaly.notes,
          },
        ]
      : [],
    pendingNotes: [],
    startTime,
    endTime,
    streams: anomaly.streams ?? [],
    criticality: anomaly.criticality,
    indicators: anomaly.indicator ? [anomaly.indicator] : [],
    rootCauses: anomaly.rootCause ? [anomaly.rootCause] : [],
    actions: anomaly.action ? [anomaly.action] : [],
    confidence: anomaly.confidence,
    savings: anomaly.estimatedSavings,
    userNotes: "",
    createdByUserName: anomaly.author,
  };
}

interface InsightsChartSnapshotProps {
  item: ProcessedInsightRow;
  asset?: InsightsAsset;
  containerHeight: number;
}

const InsightsChartSnapshot = ({
  item,
  asset,
  containerHeight,
}: InsightsChartSnapshotProps) => {
  const localizationService = useLocalization();
  const endpoints = useMemo(
    () => asset?.assetConfigurationDetails?.endpoints ?? [],
    [asset?.assetConfigurationDetails?.endpoints]
  );

  const selectedEndpoint = useMemo((): EndpointDetailsDto | null => {
    if (item.pointId) {
      const found = endpoints.find((endpoint) => endpoint.id === item.pointId);
      if (found) {
        return found as EndpointDetailsDto;
      }
    }

    return (endpoints[0] as EndpointDetailsDto) ?? null;
  }, [endpoints, item.pointId]);

  const initialChartEventFilter = useMemo(() => {
    const maxEndDate = moment().endOf("day");
    // Ongoing anomalies have no end date (null/invalid): anchor the chart range to "now".
    const endBasis = isOngoingEndDate(item.endDate) ? moment() : moment(item.endDate);
    const requestedEndDate = endBasis.add(CHART_CONTEXT_DAYS, "days").endOf("day");

    return {
      startDate: moment(item.startDate).subtract(CHART_CONTEXT_DAYS, "days").startOf("day").toDate(),
      endDate: moment.min(requestedEndDate, maxEndDate).toDate(),
    };
  }, [item.endDate, item.startDate]);

  const [eventFilter, setEventFilter] = useState(initialChartEventFilter);

  useEffect(() => {
    setEventFilter(initialChartEventFilter);
  }, [initialChartEventFilter]);

  const endpointFeatureQuery = useQuery(
    ["endpointFeatureQuery", selectedEndpoint?.id],
    () => siteExplorerApiService.getEndpointFeature(selectedEndpoint!.id),
    { enabled: !!selectedEndpoint?.id }
  );

  const endpointEventsStateChartDataQuery = useQuery(
    ["endpointEventsStateChartData", selectedEndpoint?.id, eventFilter.startDate, eventFilter.endDate],
    () =>
      assetsDashboardApiService.getEndpointEventsStateChartData(
        selectedEndpoint!.id,
        eventFilter.startDate,
        eventFilter.endDate
      ),
    { enabled: !!selectedEndpoint?.id }
  );

  if (!selectedEndpoint) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center text-center text-sm text-gray-500">
        {localizationService.toLanguageString(
          "custom.noPointSelectedForAnomalyChart",
          enMessages.custom.noPointSelectedForAnomalyChart
        )}
      </div>
    );
  }

  if (endpointFeatureQuery.isLoading) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const chartHeight = Math.max(220, containerHeight - 240);
  const machineryType = asset?.machineryType;
  const snapshotChartKind = getInsightsSnapshotChartKind(item);
  const chartContent =
    snapshotChartKind === "activity" ? (
      <div className="min-h-0 flex-1 overflow-hidden">
        <EndpointActivityTabPage
          eventFilter={eventFilter}
          selectedItem={selectedEndpoint.id}
          selectedItemSerialNo={selectedEndpoint.serialNumber}
          endpointFeature={endpointFeatureQuery.data}
          showTables={false}
          isSimpleChart={false}
          showChartTools={false}
          chartParentHeight={chartHeight}
          machineryType={machineryType}
          greasePumpData={endpointEventsStateChartDataQuery.data}
          showLegendOverride={true}
          queryStaleTime={10 * 60 * 1000}
        />
      </div>
    ) : (
      <div className="min-h-0 flex-1 overflow-hidden">
        <EndpointDiagnosticsTabPage
          eventFilter={eventFilter}
          selectedItem={selectedEndpoint.serialNumber}
          selectedEndpointId={selectedEndpoint.id}
          endpointFeature={endpointFeatureQuery.data}
          showTables={false}
          isSimpleChart={false}
          showChartTools={false}
          chartParentHeight={chartHeight}
          greasePumpData={endpointEventsStateChartDataQuery.data}
          showLegendOverride={true}
          queryStaleTime={10 * 60 * 1000}
        />
      </div>
    );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      {chartContent}
    </div>
  );
};

export default function InsightsAnomalyDetailPanel({
  item,
  asset,
  fetchDetailData = false,
}: InsightsAnomalyDetailPanelProps): React.ReactElement {
  const { height } = useWindowSize();
  const localizationService = useLocalization();
  const user = useIdentityStore((store) => store.user);
  const userId = user?.profile?.sub ?? undefined;
  const userName = user?.profile?.preferred_username ?? undefined;
  const endpointId = item?.pointId ?? "";
  const containerHeight = Math.max(520, height - 280);
  const [resetVersion, setResetVersion] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);

  const rawData =
    item && (isBackendAnomaly(item.rawAnomaly) || isAnchorItem(item.rawAnomaly)) ? item.rawAnomaly.data : null;
  const persistedId = rawData?.id || undefined;

  const detailAssetQuery = useQuery<InsightsAsset>(
    ["insightsDetailAsset", item?.machineId],
    () => assetsDashboardApiService.getAssetMetrics(item!.machineId!),
    { enabled: fetchDetailData && !!item?.machineId }
  );

  const fullItemQuery = useQuery<InsightBackendPayload | MlAnchorDto | null>(
    ["insightsDetailItem", item?.itemKind, persistedId],
    () =>
      item!.itemKind === "anchor"
        ? anomalyApiService.getAnchorPayload(persistedId!)
        : anomalyApiService.getAnomalyPayload(persistedId!),
    { enabled: fetchDetailData && !!persistedId && item?.itemKind !== "table" }
  );

  const effectiveAsset = asset ?? (fetchDetailData ? detailAssetQuery.data : undefined);
  const effectiveItem = useMemo((): ProcessedInsightRow | null => {
    if (!item || !fetchDetailData || !fullItemQuery.data) {
      return item;
    }

    if (item.itemKind === "anchor") {
      return { ...item, rawAnomaly: { kind: "anchor", data: fullItemQuery.data as MlAnchorDto } };
    }

    if (item.itemKind === "anomaly") {
      return { ...item, rawAnomaly: { kind: "anomaly", data: fullItemQuery.data as InsightBackendPayload } };
    }

    return item;
  }, [item, fetchDetailData, fullItemQuery.data]);
  const band = useMemo(() => (effectiveItem ? toAnomalyBand(effectiveItem) : null), [effectiveItem]);

  useEffect(() => {
    setResetVersion(0);
    setIsEditMode(false);
  }, [item?.id]);

  if (!item) {
    return (
      <div className="flex h-full min-h-[420px] w-full flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white text-center text-sm text-gray-500">
        {localizationService.toLanguageString(
          "custom.selectAnomalyToViewDetails",
          enMessages.custom.selectAnomalyToViewDetails
        )}
      </div>
    );
  }

  if (!endpointId) {
    return (
      <div className="flex h-full min-h-[420px] items-center justify-center text-center text-sm text-gray-500">
        {localizationService.toLanguageString(
          "custom.noPointSelectedForAnomalyDetails",
          enMessages.custom.noPointSelectedForAnomalyDetails
        )}
      </div>
    );
  }

  if (!band) {
    return (
      <div className="flex h-full min-h-[420px] items-center justify-center text-center text-sm text-gray-500">
        {localizationService.toLanguageString(
          "custom.unableToPrepareAnomalyDetails",
          enMessages.custom.unableToPrepareAnomalyDetails
        )}
      </div>
    );
  }

  if (fetchDetailData && (detailAssetQuery.isLoading || fullItemQuery.isLoading)) {
    return (
      <div className="flex h-full min-h-[420px] w-full flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white">
        <LoadingSpinner />
      </div>
    );
  }

  const handleCancelEdit = () => {
    setResetVersion((current) => current + 1);
    setIsEditMode(false);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleSaveSuccess = () => {
    setIsEditMode(false);
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
      <AnomalyBandProvider
        key={`${endpointId}-${band.id}-${resetVersion}`}
        endpointId={`${endpointId}-${band.id}`}
        initialBands={[band]}
        initialSelectedBandId={band.id}
        keepSingleBandSelected={true}
        readOnly={!isEditMode}
      >
        <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
          <AnomalyDetectorForm
            assetId={effectiveAsset?.id ?? ""}
            endpointId={endpointId}
            onClose={handleCancelEdit}
            userId={userId}
            userName={userName}
            chartTabContent={
              <InsightsChartSnapshot
                item={effectiveItem ?? item}
                asset={effectiveAsset}
                containerHeight={containerHeight}
              />
            }
            borderlessContent={true}
            closeOnSave={false}
            detailsReadOnly={!isEditMode}
            onEdit={handleEdit}
            onSaveSuccess={handleSaveSuccess}
          />
        </div>
      </AnomalyBandProvider>
    </div>
  );
}
